import { maskPhone, normalisePhone, type ImportedContact } from "@teamna/shared";
import { Link } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  DataTable,
  Field,
  FormSection,
  InlineError,
  Sheet,
  Switch,
  TextAreaField,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import {
  useImportContacts,
  useImportedContacts,
  useMarkSetupStep,
  useUpdateImportedContact,
  type ParsedContact,
} from "@teamna/web-client/hooks/use-setup.ts";
import { SaveBar, SetupFrame } from "./frame.tsx";
import { WhatsAppSyncButton } from "../whatsapp-sync/sync-tool.tsx";
import { phoneText } from "../families/model.ts";

/**
 * Setup step 5: import the member list (mobile spec 5.1 step 3, as a desktop page).
 *
 * Two ways in, as cards: pasted numbers, and CSV, which has its own page (`import-csv.tsx`). Whatever comes in is shown before it is
 * saved; after the save the review table lists everyone with Edit (name and number) and a Skip
 * toggle. Nobody is contacted on this page. "Save" marks the step, the same
 * `POST /setup/members_imported` the mobile review sends.
 *
 *
 * The WhatsApp sync tool comes first (the same panel as Tools, Members and Team): the owner adds the Teamna
 * number to the group and the people arrive in the review table by themselves.
 */
export function SetupImportScreen() {
  return (
    <SetupFrame step="members_imported">{(businessId) => <ImportBody businessId={businessId} />}</SetupFrame>
  );
}

/** "Sara Al Ali 050 123 4567", "050 123 4567" or "Sara, 0501234567", one per line. Mobile's parser. */
export function parsePasted(raw: string): ParsedContact[] {
  return raw
    .split(/\r?\n|;/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^(.*?)([+\d][\d\s()-]{6,})$/.exec(line);
      if (!match) return { displayName: line, phone: "" };
      const phone = match[2]!.trim();
      // A number is never a name. With no name, the list shows the masked number until the person joins.
      const normalised = normalisePhone(phone);
      return {
        displayName: match[1]!.replace(/[:,-]\s*$/, "").trim() || (normalised ? maskPhone(normalised) : phone),
        phone,
      };
    });
}

function usable(rows: ParsedContact[]): ParsedContact[] {
  return rows.filter((row) => row.phone.replace(/\D/g, "").length >= 7);
}

function ImportBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const members = label("member", { plural: true });
  const contacts = useImportedContacts(businessId);
  const importContacts = useImportContacts(businessId);
  const mark = useMarkSetupStep(businessId);

  const [mode, setMode] = useState<"none" | "paste">("none");
  const [pasted, setPasted] = useState("");
  const incoming = mode === "paste" ? parsePasted(pasted) : [];
  const ready = usable(incoming);

  function addIncoming() {
    importContacts.mutate(
      { source: "pasted", contacts: ready },
      {
        onSuccess: () => {
          showToast(`${ready.length} added to your list`);
          setPasted("");
          setMode("none");
        },
      },
    );
  }

  const card = "flex flex-col gap-3 rounded-card border bg-paper p-5 shadow-card";

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 rounded-card border border-accent bg-paper p-5 shadow-card" aria-labelledby="sync-card-title">
        <h2 id="sync-card-title" className="text-[17px]">
          Sync a WhatsApp group
        </h2>
        <p className="text-[14px] text-ink-2">
          Add the Teamna number to your group. Teamna reads the people, with their WhatsApp names, and you choose who
          each person is. Teamna does not read the messages or the profile photos.
        </p>
        <div>
          <WhatsAppSyncButton businessId={businessId} variant="primary" label="Open WhatsApp sync" />
        </div>
      </section>
      <div className="grid gap-4 md:grid-cols-2">
        <section className={`${card} ${mode === "paste" ? "border-accent" : "border-line"}`}>
          <h2 className="text-[17px]">Paste numbers</h2>
          <p className="text-[14px] text-ink-2">
            One per line. A name before the number is kept. A number with no name gets a masked name until the
            person joins.
          </p>
          <Button
            variant="secondary"
            onClick={() => setMode("paste")}
          >
            Paste numbers
          </Button>
        </section>

        <section className={`${card} border-line`}>
          <h2 className="text-[17px]">CSV file</h2>
          <p className="text-[14px] text-ink-2">
            A spreadsheet with {members}, {label("participant", { plural: true })},{" "}
            {label("group", { plural: true })} and sessions left. You match the columns and check the rows
            before anything is saved.
          </p>
          <Link
            to="/owner/setup/import/csv"
            className="mt-auto inline-flex min-h-11 items-center justify-center rounded-xl border border-line-strong bg-paper px-4 text-[14px] font-semibold text-ink hover:bg-surface"
          >
            Import a CSV file
          </Link>
        </section>
      </div>

      {mode === "paste" ? (
        <TextAreaField
          label="Numbers, one per line"
          rows={7}
          value={pasted}
          placeholder={"Sara Al Ali 050 123 4567\nMohammed 052 222 8832"}
          onChange={(event) => setPasted(event.target.value)}
        />
      ) : null}


      {incoming.length > 0 ? (
        <FormSection
          title={`${ready.length} ${ready.length === 1 ? "number" : "numbers"} ready`}
          {...(incoming.length > ready.length
            ? {
                description: `${incoming.length - ready.length} ${incoming.length - ready.length === 1 ? "line has" : "lines have"} no number we can read. They are left out.`,
              }
            : {})}
        >
          <ul className="divide-y divide-line">
            {incoming.slice(0, 8).map((row) => (
              <li
                key={`${row.displayName}|${row.phone}`}
                className="flex justify-between gap-3 py-2.5 text-[14px]"
              >
                <span className="truncate font-semibold text-ink">{row.displayName || row.phone}</span>
                <span className="shrink-0 text-ink-3 tabular-nums">{row.phone || "no number"}</span>
              </li>
            ))}
          </ul>
          {incoming.length > 8 ? (
            <p className="py-2.5 text-[13px] text-ink-3">And {incoming.length - 8} more.</p>
          ) : null}
          <div className="py-4">
            <Button disabled={ready.length === 0} loading={importContacts.isPending} onClick={addIncoming}>
              Add {ready.length} to the list
            </Button>
            {importContacts.error ? (
              <div className="mt-3">
                <InlineError error={importContacts.error} />
              </div>
            ) : null}
          </div>
        </FormSection>
      ) : null}

      <ReviewTable
        businessId={businessId}
        contacts={contacts.data?.items ?? []}
        isLoading={contacts.isLoading}
      />

      <SaveBar note="Nobody is contacted here. The invite is a later step.">
        <Button
          loading={mark.isPending}
          disabled={(contacts.data?.counts.members ?? 0) === 0}
          onClick={() =>
            mark.mutate("members_imported", {
              onSuccess: () => showToast(`${label("member", { plural: true, capital: true })} saved`),
            })
          }
        >
          Save {contacts.data?.counts.members ?? 0}{" "}
          {(contacts.data?.counts.members ?? 0) === 1 ? label("member") : members}
        </Button>
        {contacts.data && contacts.data.counts.members === 0 ? (
          // No list to bring in: the owner shares the join link in the invite step instead (decision
          // D7). The mobile onboarding has the same way out ("Share an invite link"). Only once the
          // list has loaded and is really empty: a list that is still loading is not empty (review F1.1).
          <Button
            variant="secondary"
            loading={mark.isPending}
            onClick={() =>
              mark.mutate("members_imported", {
                onSuccess: () => showToast(`Skipped. You share the join link with your ${members} in the invite step.`),
              })
            }
          >
            Skip, I share the join link
          </Button>
        ) : null}
      </SaveBar>
      {mark.error ? <InlineError error={mark.error} /> : null}
    </div>
  );
}

function reviewColumns({
  update,
  onEdit,
}: {
  update: ReturnType<typeof useUpdateImportedContact>;
  onEdit: (contact: ImportedContact) => void;
}): DataTableColumn<ImportedContact>[] {
  return [
    {
      id: "name",
      header: "Name",
      cell: (row) => (
        <span
          className={row.classification === "skip" ? "text-ink-3 line-through" : "font-semibold text-ink"}
        >
          {row.displayName}
        </span>
      ),
      sortValue: (row) => row.displayName,
    },
    { id: "phone", header: "Mobile", cell: (row) => phoneText(row.phoneE164) },
    {
      id: "source",
      header: "From",
      hideOnPhone: true,
      cell: (row) =>
        row.source === "chat_export"
          ? "Chat export"
          : row.source === "pasted"
            ? "Pasted"
            : row.source === "contacts"
              ? "Contacts"
              : "WhatsApp group",
    },
    {
      id: "joined",
      header: "Joined",
      hideOnPhone: true,
      cell: (row) => (row.matchedUserId ? <Badge tone="confirmed">Joined</Badge> : null),
    },
    {
      id: "edit",
      header: "Edit",
      width: "88px",
      cell: (row) => (
        <Button variant="ghost" aria-label={`Edit ${row.displayName}`} onClick={() => onEdit(row)}>
          Edit
        </Button>
      ),
    },
    {
      id: "skip",
      header: "Skip",
      width: "96px",
      cell: (row) => (
        <Switch
          label={`Skip ${row.displayName}`}
          checked={row.classification === "skip"}
          onChange={(next) => update.mutate({ id: row.id, classification: next ? "skip" : "member" })}
        />
      ),
    },
  ];
}

function ReviewTable({
  businessId,
  contacts,
  isLoading,
}: {
  businessId: string;
  contacts: ImportedContact[];
  isLoading: boolean;
}) {
  const { label } = useLabels();
  const update = useUpdateImportedContact(businessId);
  // A person from the WhatsApp sync tool waits there (`unsorted`) until the owner sorts them.
  const people = contacts.filter((row) => row.classification === "member" || row.classification === "skip");
  const team = contacts.filter((row) => row.classification === "team" || row.classification === "self");
  const kept = people.filter((row) => row.classification === "member").length;
  const [editing, setEditing] = useState<ImportedContact | null>(null);

  const columns = reviewColumns({ update, onEdit: setEditing });

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-[19px]">Review the list</h2>
        <p className="mt-1 text-[14px] text-ink-2">
          {kept} {kept === 1 ? label("member") : label("member", { plural: true })}, {people.length - kept}{" "}
          skipped.
          {team.length > 0
            ? ` ${team.length} ${team.length === 1 ? "person is" : "people are"} on your team and set aside.`
            : ""}
        </p>
      </div>
      <DataTable
        caption={`${label("member", { capital: true })} list`}
        columns={columns}
        rows={people}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        empty={{
          title: "Nobody on the list yet",
          message: "Connect a WhatsApp group or paste numbers above.",
        }}
        countLabel={(count) => `${count} ${count === 1 ? "person" : "people"}`}
      />
      {update.error && !editing ? <InlineError error={update.error} /> : null}
      {editing ? <EditContactSheet businessId={businessId} contact={editing} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

/**
 * Fix one contact: the name and the number. A contact with no name shows a masked number, so the
 * name field starts empty for it. The number of a person who joined is how they sign in, so it
 * stays as it is.
 */
function EditContactSheet({
  businessId,
  contact,
  onClose,
}: {
  businessId: string;
  contact: ImportedContact;
  onClose: () => void;
}) {
  const update = useUpdateImportedContact(businessId);
  const masked = contact.displayName === maskPhone(contact.phoneE164);
  const [name, setName] = useState(masked ? "" : contact.displayName);
  const [phone, setPhone] = useState(contact.phoneE164);
  const joined = contact.matchedUserId !== null;
  const formId = useId();

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const patch: { displayName?: string; phone?: string } = {};
    if (name.trim() && name.trim() !== contact.displayName) patch.displayName = name.trim();
    if (!joined && phone.trim() && phone.trim() !== contact.phoneE164) patch.phone = phone.trim();
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    update.mutate(
      { id: contact.id, ...patch },
      {
        onSuccess: () => {
          showToast("Contact saved");
          onClose();
        },
      },
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Edit contact"
      subtitle={contact.displayName}
      actions={
        <>
          <Button size="lg" fullWidth type="submit" form={formId} loading={update.isPending}>
            Save
          </Button>
          <Button variant="ghost" fullWidth onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="flex flex-col gap-4">
        <Field
          label="Name"
          value={name}
          placeholder={masked ? "No name yet" : undefined}
          autoComplete="off"
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          label="Mobile number"
          value={phone}
          inputMode="tel"
          autoComplete="off"
          disabled={joined}
          hint={joined ? "This person joined with this number. It is how they sign in." : undefined}
          onChange={(event) => setPhone(event.target.value)}
        />
        {update.error ? <InlineError error={update.error} /> : null}
      </form>
    </Sheet>
  );
}
